import { Box, Skeleton, Stack, SxProps, Theme, Typography, TypographyVariant } from '@mui/material'
import { C1Chip, C1ChipProps } from 'c1ui/components/C1Chip'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { shadows } from 'c1ui/theme/C1Theme'
import { FC, ReactNode } from 'react'

export type C1HeaderProps = {
    icon?: React.ReactElement
    title: ReactNode
    titleVariant?: TypographyVariant
    info?: ReactNode
    chip?: C1ChipProps
    buttons?: ReactNode | ReactNode[]
    customHeader?: never
    spacingVariant?: SpacingVariants
}

export type C1CustomHeaderProps = {
    icon?: never
    title?: never
    titleVariant?: never
    info?: never
    chip?: never
    buttons?: never
    customHeader: ReactNode
    spacingVariant?: SpacingVariants
}

export type C1TableGridHeaderProps = C1HeaderProps | C1CustomHeaderProps

export type C1TableGridProps = C1TableGridHeaderProps & {
    children: ReactNode
    spacingVariant?: SpacingVariants
}

export enum SpacingVariants {
    default,
    compact,
    noPadding,
}

const getSpacingSx = (spacingVariant: SpacingVariants | undefined): SxProps<Theme> => {
    switch (spacingVariant) {
        case SpacingVariants.compact:
            return { px: 0, py: 2 }
        case SpacingVariants.noPadding:
            return { p: 0 }
        // no default
    }

    return { px: 3, py: 2 }
}

const C1TableGridHeader: FC<C1TableGridHeaderProps> = ({
    icon, title, titleVariant = 'h4', info, chip, buttons,
}) => (
    <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
            borderTopLeftRadius: C1BorderRadius.large,
            borderTopRightRadius: C1BorderRadius.large,
            p: 3,
            pb: 2,
        }}
    >
        <Typography variant={titleVariant}>
            {title}
            {icon}
        </Typography>
        <Stack spacing={2} direction="row" alignItems="center">
            {info && <Box>{info}</Box>}
            {chip && <C1Chip {...chip} />}
            {Array.isArray(buttons)
                ? buttons?.map((b, i) => <span key={i}>{b}</span>)
                : buttons}
        </Stack>
    </Stack>
)

export const C1TableGrid: FC<C1TableGridProps> = ({
    children,
    spacingVariant = SpacingVariants.default,
    ...rest
}) => (
    <Stack sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: C1BorderRadius.large,
        marginBottom: 1,
        backgroundColor: 'background.default',
        width: '100%',
        pb: 3,
        boxShadow: shadows.default,
    }}
    >
        <C1TableGridHeader spacingVariant={spacingVariant} {...rest} />
        <Stack spacing={2} sx={{ ...getSpacingSx(spacingVariant) }}>{children}</Stack>
    </Stack>
)

export const C1TableGridSkeleton: FC = () => <C1TableGrid title={<Skeleton variant="text" sx={{ width: 180, height: 30 }} />}>
    <Skeleton />
    <Skeleton />
    <Skeleton />
</C1TableGrid>
